import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { User, Lock, Sun, Moon, Save, Check, ChevronRight, IdCard, X, KeyRound } from "lucide-react";
import PushSettings from "@/components/PushSettings";
import ConnectTools from "@/components/ConnectTools";

function splitName(full) {
  if (!full) return { first: "", last: "" };
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}
export default function Settings() {
  const { user, refresh } = useAuth();
  const { theme, setTheme } = useTheme();
  const initial = splitName(user?.name);
  const [firstName, setFirstName] = useState(user?.first_name ?? initial.first);
  const [lastName, setLastName] = useState(user?.last_name ?? initial.last);
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const dirty =
    firstName !== (user?.first_name ?? splitName(user?.name).first) ||
    lastName !== (user?.last_name ?? splitName(user?.name).last) ||
    phone !== (user?.phone ?? "");

  const saveProfile = async () => {
    if (!firstName.trim()) {
      toast.error("First name is required");
      return;
    }
    setSavingProfile(true);
    try {
      await api.put("/auth/me", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
      });
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="settings-page">
      <div className="section-tag mb-3"><span className="dot" /> Account</div>
      <h1 className="h-display text-3xl sm:text-4xl">Settings</h1>
      <p className="text-sm mt-2 mb-8" style={{ color: "var(--text-muted)" }}>
        Update your profile, password, and how the app looks.
      </p>

      {/* Profile */}
      <div className="surface-card p-6 mb-6" data-testid="profile-card">
        <div className="flex items-center gap-2 mb-1">
          <User size={16} style={{ color: "var(--lime)" }} />
          <div className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>Profile</div>
        </div>
        <p className="text-xs mb-5" style={{ color: "var(--text-muted)" }}>Your name, email, and phone tied to your account.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="field-label">First name <span className="req">*</span></label>
            <input
              className="input-base"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Alex"
              data-testid="settings-first-name-input"
            />
          </div>
          <div>
            <label className="field-label">Last name</label>
            <input
              className="input-base"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Carter"
              data-testid="settings-last-name-input"
            />
          </div>
          <div>
            <label className="field-label">Email</label>
            <input
              className="input-base"
              value={user?.email || ""}
              disabled
              data-testid="settings-email-input"
              style={{ opacity: 0.7, cursor: "not-allowed" }}
            />
            <p className="text-[11px] mt-1" style={{ color: "var(--text-dim)" }}>Email can&apos;t be changed. Contact your coach if needed.</p>
          </div>
          <div>
            <label className="field-label">Phone number</label>
            <input
              className="input-base"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              data-testid="settings-phone-input"
            />
          </div>
        </div>

        <div className="mt-5">
          <button
            onClick={saveProfile}
            disabled={savingProfile || !dirty}
            className="btn-primary flex items-center gap-2 text-sm"
            data-testid="settings-save-profile-button"
          >
            {savingProfile ? <span className="spinner" /> : <Save size={14} />}
            {savingProfile ? "Saving..." : "Save profile"}
          </button>
        </div>
      </div>

      {/* Password */}
      <div className="surface-card p-6 mb-6" data-testid="password-card">
        <div className="flex items-center gap-2 mb-1">
          <Lock size={16} style={{ color: "var(--lime)" }} />
          <div className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>Password</div>
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            Set a new password to keep your account secure. Min. 8 characters.
          </p>
          <button
            onClick={() => setPasswordOpen(true)}
            className="btn-ghost text-sm flex items-center gap-2 shrink-0"
            data-testid="settings-open-password-modal-button"
          >
            <KeyRound size={14} /> Reset password
          </button>
        </div>
      </div>

      <ConnectTools />

      <PushSettings />

      <CoachAccessCard />

      {/* Theme */}
      <div className="surface-card p-6 mb-6" data-testid="theme-card">
        <div className="flex items-center gap-2 mb-1">
          <Sun size={16} style={{ color: "var(--lime)" }} />
          <div className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>Appearance</div>
        </div>
        <p className="text-xs mb-5" style={{ color: "var(--text-muted)" }}>Choose how the app looks. Your choice is saved on this device.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ThemeOption
            active={theme === "dark"}
            onClick={() => setTheme("dark")}
            icon={Moon}
            label="Dark"
            description="Classic lime on black. Easy on the eyes at night."
            testid="theme-dark"
          />
          <ThemeOption
            active={theme === "light"}
            onClick={() => setTheme("light")}
            icon={Sun}
            label="Light"
            description="White background, lime accents. Great in daylight."
            testid="theme-light"
          />
        </div>
      </div>

      {/* Buyer profile shortcut */}
      <Link
        to="/profile"
        className="surface-card p-5 flex items-center gap-4 transition hover:opacity-90"
        data-testid="settings-buyer-profile-link"
      >
        <IdCard size={20} style={{ color: "var(--lime)" }} />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">Buyer / signing profile</div>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            Company name, signer, mailing address used to auto-fill contracts.
          </div>
        </div>
        <ChevronRight size={18} style={{ color: "var(--text-muted)" }} />
      </Link>

      {passwordOpen && <PasswordModal onClose={() => setPasswordOpen(false)} />}
    </div>
  );
}

function ThemeOption({ active, onClick, icon: Icon, label, description, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-left p-4 rounded-2xl transition"
      style={{
        border: active ? "1px solid var(--lime)" : "1px solid var(--border)",
        background: active ? "var(--lime-glow)" : "var(--surface2)",
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={16} style={{ color: active ? "var(--lime)" : "var(--text-muted)" }} />
          <span className="font-semibold text-sm" style={{ color: active ? "var(--lime)" : "var(--text)" }}>
            {label}
          </span>
        </div>
        {active && <Check size={16} style={{ color: "var(--lime)" }} />}
      </div>
      <p className="text-xs" style={{ color: "var(--text-muted)" }}>{description}</p>
    </button>
  );
}

function PasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!current || !newPwd || !confirmPwd) {
      toast.error("Fill all password fields");
      return;
    }
    if (newPwd.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("New password and confirmation don't match");
      return;
    }
    if (current === newPwd) {
      toast.error("New password must be different from current");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/me/change-password", { current_password: current, new_password: newPwd });
      toast.success("Password updated");
      onClose();
    } catch (err) {
      toast.error("Could not change password", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return createPortal((
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="password-modal"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-xl"
        style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2">
            <KeyRound size={16} style={{ color: "var(--lime)" }} />
            <h2 className="font-mono text-sm uppercase tracking-wider" style={{ color: "var(--lime)" }}>Reset password</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 transition hover:opacity-70" data-testid="password-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="field-label">Current password</label>
            <input
              type="password"
              className="input-base"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              autoFocus
              data-testid="password-modal-current-input"
            />
          </div>
          <div>
            <label className="field-label">New password</label>
            <input
              type="password"
              className="input-base"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              autoComplete="new-password"
              data-testid="password-modal-new-input"
            />
            <p className="text-[11px] mt-1" style={{ color: "var(--text-dim)" }}>At least 8 characters.</p>
          </div>
          <div>
            <label className="field-label">Confirm new password</label>
            <input
              type="password"
              className="input-base"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              autoComplete="new-password"
              data-testid="password-modal-confirm-input"
            />
          </div>
        </div>

        <div className="p-5 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            className="btn-primary text-sm flex items-center gap-2"
            data-testid="password-modal-submit-button"
          >
            {busy ? <span className="spinner" /> : <Lock size={14} />}
            {busy ? "Updating..." : "Update password"}
          </button>
        </div>
      </form>
    </div>
  ), document.body);
}

function CoachAccessCard() {
  const [on, setOn] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/auth/me/coach-access")
      .then(({ data }) => setOn(data.coach_sees_pipeline !== false))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const toggle = async () => {
    const next = !on;
    setOn(next);
    try {
      await api.put("/auth/me/coach-access", { coach_sees_pipeline: next });
      toast.success(next ? "AI Coach can see your numbers" : "AI Coach won't see your numbers");
    } catch (e) {
      setOn(!next);
      toast.error("Could not save that");
    }
  };

  if (!loaded) return null;

  return (
    <div className="surface-card p-6 mb-6" data-testid="coach-access-card">
      <h2 className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--lime)" }}>
        AI Coach access
      </h2>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="text-sm font-medium mb-1">Let the AI Coach see my numbers</div>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            How many leads you have at each stage, your calls and texts this week, and your goals. Never seller
            names, addresses or notes. Turning this off makes the coach's answers generic.
          </p>
        </div>
        <button
          onClick={toggle}
          role="switch"
          aria-checked={on}
          className="relative shrink-0 rounded-full transition"
          style={{
            width: 46,
            height: 26,
            background: on ? "var(--lime)" : "var(--surface2)",
            border: "1px solid var(--border)",
          }}
          data-testid="coach-access-toggle"
        >
          <span
            className="absolute rounded-full transition-all"
            style={{
              width: 18,
              height: 18,
              top: 3,
              left: on ? 24 : 3,
              background: on ? "#0c0c0c" : "var(--text-muted)",
            }}
          />
        </button>
      </div>
    </div>
  );
}
